import { useRef, useState } from 'react';
import styled from 'styled-components';
import type { KsPackageAct, KsPackageDoc } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Input, Select, Label, Field } from '@shared/ui/Input';
import {
  DOC_KINDS,
  docKindLabel,
  fmtMoney,
  packageStatusLabel,
  packageStatusTone,
  supplyLabel,
} from '@shared/lib/smetaLabels';

const TableTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-top: ${({ theme }) => theme.spacing(5)};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
    white-space: nowrap;
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

const ThRight = styled.th`
  text-align: right !important;
`;

const Inline = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const PaddedInline = styled(Inline)`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const InlineField = styled(Field)`
  margin-bottom: 0;
`;

const WideInlineField = styled(InlineField)`
  flex: 2;
`;

const CompactSelect = styled(Select)`
  min-width: 140px;
`;

const FileField = styled(Field)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const UploadDocsForm = styled.form`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Muted = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const NoDocsMuted = styled(Muted)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

export interface KsPackageDocsTabProps {
  packageId: string;
  pkgStatus: string;
  docs: KsPackageDoc[];
  acts: KsPackageAct[];
  canWrite: boolean;
  frozen: boolean;
  busy: boolean;
  building: boolean;
  importingMto: boolean;
  importingLogist: boolean;
  uploadingDocs: boolean;
  docError: string | null;
  onBuild: (id: string) => void;
  onImportMto: (id: string) => void;
  onImportLogist: (id: string) => void;
  onUploadDocs: (payload: {
    files: File[];
    docKind: string;
    docSupply: string;
    docItemKind: string;
    docActId: string;
  }) => Promise<void>;
}

export function KsPackageDocsTab({
  packageId,
  pkgStatus,
  docs,
  acts,
  canWrite,
  frozen,
  busy,
  building,
  importingMto,
  importingLogist,
  uploadingDocs,
  docError,
  onBuild,
  onImportMto,
  onImportLogist,
  onUploadDocs,
}: KsPackageDocsTabProps) {
  const [docKind, setDocKind] = useState('transfer_material');
  const [docSupply, setDocSupply] = useState('customer');
  const [docItemKind, setDocItemKind] = useState('material');
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [docActId, setDocActId] = useState('');
  const docFilesRef = useRef<HTMLInputElement>(null);

  const docActHint =
    acts.length === 1
      ? `Позиции лягут в единственный акт № ${acts[0].act_no}.`
      : 'Позиции лягут в выбранный выше акт.';

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    await onUploadDocs({
      files: docFiles,
      docKind,
      docSupply,
      docItemKind,
      docActId: docActId || (acts[0]?.id ?? ''),
    });
    setDocFiles([]);
    if (docFilesRef.current) docFilesRef.current.value = '';
  };

  return (
    <>
      <TableTitle>
        Сборка состава
        <Badge $tone={packageStatusTone(pkgStatus)}>
          {packageStatusLabel(pkgStatus)}
        </Badge>
      </TableTitle>
      <Hint>
        Сборка материализует строки из фактов объёмов Прораба, журнала ПТО и втянутой
        первички, считает итоги по статьям затрат и заводит находки. Идёт очередью —
        статус обновляется сам. Ручные правки и решения по находкам пересборку
        переживают.
      </Hint>
      <PaddedInline>
        <Button
          $variant="primary"
          $size="sm"
          type="button"
          onClick={() => onBuild(packageId)}
          disabled={building || !canWrite || frozen || busy}
          title={!canWrite ? 'Только чтение' : undefined}
        >
          {busy ? 'Собирается…' : 'Собрать'}
        </Button>
        <Button
          $variant="secondary"
          $size="sm"
          type="button"
          onClick={() => onImportMto(packageId)}
          disabled={importingMto || !canWrite || frozen || busy}
          title="Втянуть принятые поставки из МТО за период пакета в первичку"
        >
          {importingMto ? 'Втягиваю МТО…' : 'Втянуть поставки из МТО'}
        </Button>
        <Button
          $variant="secondary"
          $size="sm"
          type="button"
          onClick={() => onImportLogist(packageId)}
          disabled={importingLogist || !canWrite || frozen || busy}
          title="Втянуть завершённые рейсы из Логиста за период пакета в компенсируемые затраты"
        >
          {importingLogist ? 'Втягиваю рейсы…' : 'Втянуть рейсы из Логиста'}
        </Button>
      </PaddedInline>

      <TableTitle>
        Первичные документы <Badge $tone="neutral">{docs.length}</Badge>
      </TableTitle>
      {docs.length === 0 ? (
        <NoDocsMuted>
          Документов нет. Загрузите накладные и акты передачи МТР — из них берутся
          материалы строк, ведомости М-19/ВВДМ и перечень приложений.
        </NoDocsMuted>
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Вид</th>
                <th>Документ</th>
                <th>Дата</th>
                <th>Контрагент</th>
                <th>Снабжение</th>
                <ThRight>Сумма</ThRight>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id}>
                  <td>{docKindLabel(d.doc_kind)}</td>
                  <td>{d.doc_no ?? 'б/н'}</td>
                  <td>{d.doc_date ?? '—'}</td>
                  <td>{d.counterparty ?? '—'}</td>
                  <td>{supplyLabel(d.supply)}</td>
                  <td className="num">{fmtMoney(d.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {!frozen && (
        <UploadDocsForm onSubmit={handleUpload}>
          <Inline>
            <WideInlineField>
              <Label>Вид документа</Label>
              <Select value={docKind} onChange={(e) => setDocKind(e.target.value)}>
                {DOC_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </Select>
            </WideInlineField>
            <InlineField>
              <Label>Снабжение</Label>
              <Select value={docSupply} onChange={(e) => setDocSupply(e.target.value)}>
                <option value="customer">поставка Заказчика</option>
                <option value="contractor">поставка Подрядчика</option>
              </Select>
            </InlineField>
            <InlineField>
              <Label>Что в документе</Label>
              <Select
                value={docItemKind}
                onChange={(e) => setDocItemKind(e.target.value)}
              >
                <option value="material">материалы</option>
                <option value="equipment">оборудование</option>
              </Select>
            </InlineField>
            {acts.length > 1 && (
              <InlineField>
                <Label>В акт</Label>
                <CompactSelect
                  value={docActId}
                  onChange={(e) => setDocActId(e.target.value)}
                >
                  {acts.map((a) => (
                    <option key={a.id} value={a.id}>
                      № {a.act_no}
                    </option>
                  ))}
                </CompactSelect>
              </InlineField>
            )}
          </Inline>
          <FileField>
            <Label>Файлы первички</Label>
            <Input
              ref={docFilesRef}
              type="file"
              multiple
              accept=".xlsx,.xlsm"
              onChange={(e) => setDocFiles(Array.from(e.target.files ?? []))}
            />
            <Hint>
              Excel-выгрузки М-15 / ТОРГ-12 / актов передачи. Выбрано файлов:{' '}
              {docFiles.length}. {docActHint} Повторная загрузка того же файла дублей не
              заводит — документ узнаётся по содержимому.
            </Hint>
          </FileField>
          {docError && <Err>{docError}</Err>}
          <Button
            type="submit"
            $variant="secondary"
            $size="sm"
            disabled={uploadingDocs || !canWrite}
            title={!canWrite ? 'Только чтение' : undefined}
          >
            {uploadingDocs ? 'Загружаю…' : 'Загрузить первичку'}
          </Button>
        </UploadDocsForm>
      )}
    </>
  );
}
